import { useLayoutEffect, useState } from "react";

// Space between two stacked toasts, in px. variables.css has no spacing token to take it from.
export const TOAST_GAP = 12;

// A toast's offset is the height of every toast above it plus one gap for each of them.
export const computeToastOffsets = (heights, gap) => {
  let top = 0;

  return heights.map(height => {
    const offset = top;
    top += height + gap;
    return offset;
  });
};

// Mounted toasts in mount order: { id, node, setOffset }.
let entries = [];
let nextId = 1;

// Measuring the stack makes the browser resolve a new toast's style at offset 0, so `.feedback`'s `transition: all` would
// animate the offset it gets next: the toast would appear in the top slot, over the toast already there, and slide down.
// NotificationError is visible from mount, so that slide could be seen. The new toast is put in its slot with the
// transition off; toasts already up still slide when the stack changes.
const placeWithoutTransition = (node, offset) => {
  const { style } = node;
  const transition = style.getPropertyValue("transition");

  style.setProperty("transition", "none");
  style.setProperty("--toast-offset", `${offset}px`);
  // Reading layout applies the new position while the transition is off.
  node.getBoundingClientRect();
  // An empty value removes the inline declaration again, so `.feedback`'s own transition applies.
  style.setProperty("transition", transition);
};

const notify = placedId => {
  const offsets = computeToastOffsets(
    entries.map(entry => entry.node.getBoundingClientRect().height),
    TOAST_GAP
  );

  entries.forEach((entry, index) => {
    if (entry.id === placedId) {
      placeWithoutTransition(entry.node, offsets[index]);
    }

    entry.setOffset(offsets[index]);
  });
};

export const registerToast = (node, setOffset) => {
  const id = nextId;
  nextId += 1;
  entries = [...entries, { id, node, setOffset }];
  notify(id);

  return id;
};

export const releaseToast = id => {
  if (!entries.some(entry => entry.id === id)) {
    return;
  }

  entries = entries.filter(entry => entry.id !== id);
  notify();
};

/**
 * Returns the toast's offset from the top slot, in px. While `active` the toast holds a slot in the stack; when it hides
 * or unmounts the slot is released, so the toasts below move up and a toast shown again goes to the bottom.
 */
export const useToastOffset = (ref, active) => {
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    if (!active || !ref.current) {
      return undefined;
    }

    const id = registerToast(ref.current, setOffset);

    return () => releaseToast(id);
  }, [ref, active]);

  return offset;
};
